import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { NAV_SECTIONS } from "@/data/site";

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => {
      const sections = NAV_SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
      const offset = 120;
      let current = "home";
      for (const sec of sections) {
        if (sec.getBoundingClientRect().top - offset <= 0) {
          current = sec.id;
        }
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 glass-strong border-b border-white/5"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <a href="#home" onClick={handleClick("home")} className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="logo-card-back" aria-hidden="true" />
          <span className="font-serif-display text-xl text-white tracking-wide leading-none">
            Verna Carr
            <span className="block text-[0.65rem] tracking-[0.25em] uppercase font-body mt-0.5" style={{ color: "var(--cs-cyan)" }}>
              Tarot Readings
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_SECTIONS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={handleClick(l.id)}
              data-testid={`nav-${l.id}`}
              className="relative px-3 py-2 text-sm tracking-wide transition-colors"
              style={{ color: active === l.id ? "var(--cs-cyan)" : "#CFD3F0" }}
            >
              {l.label}
              {active === l.id && (
                <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full" style={{ backgroundColor: "var(--cs-cyan)" }} />
              )}
            </a>
          ))}
          <button onClick={handleClick("contact")} className="btn-pill-primary ml-3 text-sm" data-testid="nav-book-session">
            Book a Session
          </button>
        </nav>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-white"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-strong border-t border-white/5" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_SECTIONS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={handleClick(l.id)}
                data-testid={`mobile-nav-${l.id}`}
                className="px-4 py-3 rounded-xl text-base transition-colors"
                style={{ color: active === l.id ? "var(--cs-cyan)" : "#E2E8F0", background: active === l.id ? "rgba(98,195,216,0.08)" : "transparent" }}
              >
                {l.label}
              </a>
            ))}
            <button onClick={handleClick("contact")} className="btn-pill-primary mt-3 text-sm" data-testid="mobile-nav-book-session">
              Book a Session
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
